import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function getImpersonationHeaders(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem("bac_impersonation");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.expiresAt < Date.now()) return {};
    if (!data.userId || !data.sessionId) return {};
    return {
      "X-Impersonate-User-Id": data.userId,
      "X-Impersonation-Session-Id": data.sessionId,
    };
  } catch {
    return {};
  }
}

function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...getImpersonationHeaders(),
  };
}

export class SubscriptionsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "SubscriptionsApiError";
    this.status = status;
  }
}

/** Only these stable, client-safe codes may escape subscription API responses. */
export type SubscriptionApiErrorCode =
  | "membership_unavailable"
  | "region_unavailable"
  | "plans_not_configured"
  | "webhook_unavailable"
  | "pricing_unavailable"
  | "already_subscribed"
  | "billing_account_unavailable"
  | "no_billing_account"
  | "subscription_region_mismatch"
  | "invalid_plan_selection"
  | "billing_unavailable"
  | "feature_unavailable"
  | "impersonation_forbidden"
  | "account_unavailable"
  | "rate_limited"
  | "session_expired"
  | "request_failed";

const SAFE_SUBSCRIPTION_ERRORS: Record<string, SubscriptionApiErrorCode> = {
  "Membership currently unavailable": "membership_unavailable",
  "Plan region currently unavailable": "region_unavailable",
  "Billing region currently unavailable": "region_unavailable",
  "Subscription products not configured": "plans_not_configured",
  "Price not found for selected plan": "plans_not_configured",
  "Subscription webhook not configured": "webhook_unavailable",
  "Subscription pricing currently unavailable": "pricing_unavailable",
  "You already have an active subscription. Use the billing portal to manage it.": "already_subscribed",
  "Billing account currently unavailable": "billing_account_unavailable",
  "No billing account found": "no_billing_account",
  subscription_region_mismatch: "subscription_region_mismatch",
  "Invalid tier or interval": "invalid_plan_selection",
  "Stripe not configured": "billing_unavailable",
  "This feature is currently unavailable": "feature_unavailable",
  "Insufficient permissions for impersonation": "impersonation_forbidden",
  "Impersonation session ID required": "impersonation_forbidden",
  "Invalid or expired impersonation session": "impersonation_forbidden",
  account_suspended: "account_unavailable",
  "User profile not found": "billing_account_unavailable",
  "Authentication required": "session_expired",
  "Invalid token": "session_expired",
  "Stripe not configured for region us": "billing_unavailable",
  "Stripe not configured for region intl": "billing_unavailable",
  session_expired: "session_expired",
};

function safeApiErrorCode(raw: unknown, status: number): SubscriptionApiErrorCode {
  if (typeof raw === "string" && SAFE_SUBSCRIPTION_ERRORS[raw]) {
    return SAFE_SUBSCRIPTION_ERRORS[raw];
  }
  if (status === 429) return "rate_limited";
  return "request_failed";
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetch(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        });
        if (retryRes.ok) return retryRes.json();
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw new SubscriptionsApiError(safeApiErrorCode(retryBody.error, retryRes.status), retryRes.status);
      }
      // No fresh token — session is truly dead.
      forceSessionExpiredRedirect();
    }
    throw new SubscriptionsApiError(safeApiErrorCode(body.error, res.status), res.status);
  }
  return res.json();
}

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new SubscriptionsApiError(safeApiErrorCode(body.error, res.status), res.status);
  }
  return res.json();
}

export interface OwnerSubscription {
  id: number;
  user_id: string;
  stripe_subscription_id: string;
  tier: "classic" | "premier";
  status: "active" | "past_due" | "canceled" | "trialing";
  billing_interval: "monthly" | "yearly" | "manual";
  /** Server-derived billing source; fall back to getBillingSource(). */
  billing_source?: BillingSource;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
}

export type BillingSource = "stripe" | "members" | "complimentary";

const MEMBERS_ID_PREFIXES = ["members_manual_", "memberful_fallback_", "migrated_memberful_", "memberful_"];

/** Resolve billing source: trust server value, else derive from the ID. */
export function getBillingSource(sub: Pick<OwnerSubscription, "stripe_subscription_id" | "billing_source"> | null | undefined): BillingSource | null {
  if (!sub) return null;
  if (sub.billing_source === "stripe" || sub.billing_source === "members" || sub.billing_source === "complimentary") return sub.billing_source;
  const id = typeof sub.stripe_subscription_id === "string" ? sub.stripe_subscription_id : "";
  if (/^sub_[A-Za-z0-9]+$/.test(id)) return "stripe";
  if (MEMBERS_ID_PREFIXES.some((p) => id.startsWith(p))) return "members";
  return "complimentary";
}

export interface SubscriptionPlans {
  tiers: {
    classic: TierInfo;
    premier: TierInfo;
  };
  configured: boolean;
  /** Checkout region resolved server-side (owner's active-car country). */
  region?: "us" | "intl";
  /** Currency Stripe will actually charge — render prices in exactly this. */
  currency?: string;
}

export interface TierInfo {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  bookingDiscount: number;
  commissionReduction: number;
  searchBoost: number;
}

export interface SubscriptionInvoice {
  id: string;
  number: string | null;
  created: number;
  status: string | null;
  amountPaid: number;
  amountDue: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  periodStart: number;
  periodEnd: number;
}

export interface ProOwnerPricePreview {
  currency: string;
  region: "us" | "intl";
  tier: "classic" | "premier";
  interval: "monthly" | "yearly";
  usdAmount: number;
  localAmount: number | null;
  storedAmount: number | null;
  action: "unchanged" | "mint" | "update" | "fx_unavailable";
}

export interface ProOwnerSetupResult {
  success: boolean;
  dryRun: boolean;
  fxSource: string;
  fxStale: boolean;
  currencies: string[];
  skipped: string[];
  preview: ProOwnerPricePreview[];
  config?: Record<string, string>;
}

export interface OwnerAnalytics {
  period?: { days: 30 | 90; start: string; end: string; requestCohort?: "created_at" };
  dataAvailability?: {
    requests: OwnerAnalyticsAvailability;
    earnings: OwnerAnalyticsAvailability;
    bookingValue: OwnerAnalyticsAvailability;
    tripLength: OwnerAnalyticsAvailability;
    availability: OwnerAnalyticsAvailability;
    responseTime: OwnerAnalyticsAvailability;
  };
  totalViews: number;
  totalBookingRequests: number | null;
  completedBookings: number | null;
  conversionRate: number | null;
  totalEarnings: number | null;
  currency?: { code: string | null; available: boolean; reason: string | null };
  averageBookingValue?: number | null;
  bookingValueCurrency?: string | null;
  averageTripLengthDays?: number | null;
  earningsPerAvailableDay?: number | null;
  availability?: {
    availableDays: number | null;
    bookedDays: number | null;
    ownerBlockedDays?: number | null;
    available: boolean;
  };
  responseTime?: {
    averageHours: number | null;
    respondedEnquiries: number | null;
    totalEnquiries: number | null;
  };
  outcomes?: Array<{ status: string; count: number }>;
  missedDemand?: {
    count: number | null;
    items: Array<{ carId: number | null; date: string; reason: string | null }>;
  };
  opportunities?: Array<{
    type: "slow_response" | "expired_requests" | "missed_availability" | "weak_conversion" | "price_below_range" | "price_above_range";
    priority: number;
    evidence: Record<string, number | string | null>;
    carId?: number;
  }>;
  cars: Array<{
    id: number; make: string; model: string; year: number; views_count: number; image_url?: string | null;
    requests?: number | null; completedBookings?: number | null; conversionRate?: number | null;
    netEarnings?: number | null; earningsCurrency?: string | null; bookedDays?: number | null; availableDays?: number | null;
    averageResponseHours?: number | null;
    price_driving?: number | null; currency_code?: string | null;
    priceIntelligence?: {
      available: boolean;
      reason: string | null;
      currentRate: number | null;
      currency: string | null;
      rangeLow: number | null;
      rangeHigh: number | null;
      median: number | null;
      sampleSize: number;
      confidence: "medium" | "high" | null;
      source: "realized_bookings" | "active_listings" | null;
    };
  }>;
  earningsTrend: Array<{ date: string; amount: number }>;
}

export interface OwnerAnalyticsAvailability {
  available: boolean;
  reason: "query_failed" | "mixed_currencies" | "unknown_currency" | "no_qualifying_data" | "no_active_listings" | "insufficient_comparables" | null;
  sampleSize?: number;
}

export interface AdminSubscriptionData {
  subscriptions: Array<OwnerSubscription & { users: { id: string; first_name: string; last_name: string; email: string } }>;
  total: number;
  summary: {
    totalActive: number;
    classicActive: number;
    premierActive: number;
    estimatedMrr: number;
  };
}

export const subscriptionsApi = {
  // Token is optional: when present the server resolves the caller's checkout
  // region (us→USD / intl→GBP) so the landing page shows the charged currency.
  getPlans: (token?: string) =>
    token
      ? authFetch<SubscriptionPlans>("/subscriptions/plans", token)
      : apiFetch<SubscriptionPlans>("/subscriptions/plans"),

  // A failed membership lookup must remain distinguishable from a confirmed
  // non-member. Treating 404/503 as `subscription:null` can offer checkout to
  // an existing paid or fallback-entitled host while management is unavailable.
  getCurrentSubscription: (token: string) =>
    authFetch<{ subscription: OwnerSubscription | null; supportPhone?: string | null }>(
      "/subscriptions/current",
      token,
    ),

  getInvoices: (token: string) =>
    authFetch<{ invoices: SubscriptionInvoice[]; degraded?: boolean }>(
      "/subscriptions/invoices",
      token,
    ),

  createCheckout: (token: string, tier: string, interval: string) =>
    authFetch<{ url: string }>("/subscriptions/create-checkout", token, {
      method: "POST",
      body: JSON.stringify({ tier, interval }),
    }),

  createPortal: (token: string) =>
    authFetch<{ url: string }>("/subscriptions/create-portal", token, {
      method: "POST",
    }),

  getOwnerAnalytics: (token: string, days?: number) =>
    authFetch<OwnerAnalytics>(`/subscriptions/owner-analytics${days ? `?days=${days}` : ""}`, token),

  adminListSubscriptions: (token: string, page?: number, tier?: string, status?: string) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    if (tier) params.set("tier", tier);
    if (status) params.set("status", status);
    return authFetch<AdminSubscriptionData>(`/admin/subscriptions?${params}`, token);
  },

  adminGetUserSubscription: (token: string, userId: string) =>
    authFetch<{ subscription: OwnerSubscription | null }>(`/admin/subscriptions/user/${encodeURIComponent(userId)}`, token),

  adminGrantSubscription: (token: string, userId: string, tier: string, billingSource?: "members" | "complimentary") =>
    authFetch<{ success: boolean }>("/admin/subscriptions/grant", token, {
      method: "POST",
      body: JSON.stringify(billingSource ? { userId, tier, billingSource } : { userId, tier }),
    }),

  adminRevokeSubscription: (token: string, userId: string, options?: { manualOnly?: boolean }) =>
    authFetch<{ success: boolean }>("/admin/subscriptions/revoke", token, {
      method: "POST",
      body: JSON.stringify(options?.manualOnly ? { userId, manualOnly: true } : { userId }),
    }),

  setupProducts: (token: string, region: "us" | "intl", dryRun: boolean, expectedAmounts?: Record<string, number | null>) =>
    authFetch<ProOwnerSetupResult>("/subscriptions/setup-products", token, {
      method: "POST",
      body: JSON.stringify({ region, dryRun, expectedAmounts }),
    }),

  // Task #8066 — Memberful → platform billing migration (dry-run + execute).
  memberfulReconcile: (token: string, csv: string, region?: string) =>
    authFetch<{ region: string; report: MemberfulReconcileReport }>("/subscriptions/memberful/reconcile", token, {
      method: "POST",
      body: JSON.stringify({ csv, region }),
    }),

  memberfulExecute: (token: string, csv: string, memberfulStripeAccountId: string, exportedOn: string, region?: string, memberfulIds?: string[]) =>
    authFetch<{
      region: string;
      summary: MemberfulExecuteSummary;
      results: MemberfulExecuteResult[];
      report: MemberfulReconcileReport;
    }>("/subscriptions/memberful/execute", token, {
      method: "POST",
       body: JSON.stringify({ csv, region, memberfulIds, memberfulStripeAccountId, exportedOn }),
    }),
};

// ── Task #8066 types ──────────────────────────────────────────────────

export interface MemberfulReconRow {
  rowNumber: number;
  memberfulId: string;
  fullName: string;
  email: string;
  active: boolean;
  autoRenew: boolean;
  tier: "classic" | "premier" | null;
  interval: "monthly" | "yearly" | null;
  amountUsd: number | null;
  paidThrough: string | null;
  userId: number | null;
  matchMethod: string | null;
  candidateMatches: Array<{ method: string; userId: number }>;
  identityConflict: boolean;
  stripeCustomerId: string | null;
  stripeCustomerOk: boolean | null;
  paymentMethodOk: boolean | null;
  existingMigratedSubId: string | null;
  otherActiveStripeSubs: string[];
  action: "create_subscription" | "fallback_entitlement" | "already_migrated" | "skip";
  reasons: string[];
  notes: string[];
}

export interface MemberfulReconcileReport {
  generatedAt: string;
  stripeAccountId: string | null;
  stripeChecked: boolean;
  csvErrors: string[];
  totals: {
    csvRows: number;
    members: number;
    activeMembers: number;
    duplicateRows: number;
    matchedActiveMembers: number;
    identityExceptions: number;
    createSubscription: number;
    fallbackEntitlement: number;
    alreadyMigrated: number;
    skipped: number;
    unmatchedUsers: number;
  };
  rows: MemberfulReconRow[];
}

export interface MemberfulExecuteSummary {
  total: number;
  succeeded: number;
  failed: number;
  subscriptionsCreated: number;
  fallbackEntitlements: number;
  alreadyMigrated: number;
}

export interface MemberfulExecuteResult {
  memberfulId: string;
  email: string;
  userId: number | null;
  action: string;
  ok: boolean;
  stripeSubscriptionId: string | null;
  firstChargeAt: string | null;
  detail: string;
}
